import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Hybrid date picker:
 * - Desktop: Radix Popover + shadcn Calendar (visual calendar)
 * - Mobile: Native <input type="date"> overlay (reliable touch, works in modals)
 */
export function DatePickerField({
  date,
  onDateChange,
  className = '',
  buttonClassName = 'w-full justify-start text-left font-normal',
  displayFormat = 'PPP',
  placeholder = 'Select date',
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const displayText = date ? format(date, displayFormat) : placeholder;

  if (isMobile) {
    const inputValue = date ? format(date, 'yyyy-MM-dd') : '';
    return (
      <div className={`relative ${className}`}>
        <Button type="button" variant="outline" className={buttonClassName} tabIndex={-1}>
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayText}
        </Button>
        <input
          type="date"
          value={inputValue}
          onChange={(e) => {
            const val = e.target.value;
            if (val) {
              const [y, m, d] = val.split('-').map(Number);
              onDateChange(new Date(y, m - 1, d));
            }
          }}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          data-testid="native-date-input"
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className={buttonClassName} data-testid="desktop-date-trigger">
            <CalendarIcon className="mr-2 h-4 w-4" />
            {displayText}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[9999]" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              if (d) onDateChange(d);
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
